<template>
  <div class="wrapper flex justify-center align-items-center" v-if="loading">
    <div class="progress-container">
      <p class="q-mb-md">
        <img
          v-if="isBobbieContext"
          class="airteam-logo"
          src="@/assets/model/bobbie_logo.svg"
        />
        <img v-else class="airteam-logo" src="@/assets/model/airteam_transparent.svg" />
        <span v-if="!isBobbieContext">AIRTEAM</span>
      </p>
      <q-linear-progress
        :value="loadingProgress"
        size="md"
        color="blue"
        rounded
        track-color="white"
      />
      <p class="progress-hint q-mt-md">Loading 3D model</p>
    </div>
  </div>
  <div v-show="!loading" class="viewer-wrapper row">
    <div v-show="!sidebarOpen" class="col-p5">
      <span class="cursor-pointer open-sidebar" @click="toggleSidebar($event)">
        <div>
          <img src="@/assets/icons/chevron-right.svg" />
        </div>
      </span>
    </div>

    <div
      v-if="!sample || project.isSampleProject"
      v-show="sidebarOpen"
      class="col-lg-2 col-md-3 col-sm-4 col-xs-12 project-panel q-pa-lg new-sidebar-layout"
      style="padding: 0"
      @click="closeEditMode"
    >
      <BreadCrumbs
        :projectId="$route.params.projectId || project.id"
        :projectName="project.name"
        :isSample="project.isSample"
      />

      <div class="sidebar-top q-pa-lg">
        <div v-if="!sample || project.isSampleProject" class="project-panel__title">
          <p class="project-panel__title-value">
            <LocationIcon
              style="margin-bottom: 5px"
              class="q-mr-sm"
              :color="activeColor"
            />
            {{ project.isSample ? project.description : project.address }}
          </p>
          <span
            v-show="!fullScreenImageActive && !csvVisualizerVisible"
            class="cursor-pointer close-sidebar"
            @click="toggleSidebar($event)"
          >
            <div>
              <img src="@/assets/icons/chevron-left.svg" />
            </div>
          </span>
        </div>
        <p v-else class="project-panel__id">
          {{ $t("sidebar.sampleProject") }}
        </p>
      </div>
      <div class="sidebar-bottom" style="padding: 2px 24px 0 24px">
        <q-expansion-item
          v-model="summaryExpanded"
          :header-class="`summary__header ${
            summaryExpanded ? 'summary__header--expanded' : ''
          }`"
          class="summary__block"
          :label="$t('sidebar.summary')"
          :caption="$t('canvas.unitOfMeasurement')"
          :expand-separator="false"
        >
          <q-card
            class="summary__card"
            :class="summaryExpanded && 'summary__card--expanded'"
          >
            <q-card-section>
              <div class="summary__content">
                <p class="project-panel__field q-mb-sm">
                  <span class="project-panel__field--label">{{
                    $t("sidebar.totalRoofArea")
                  }}</span>
                  <span class="project-panel__field--value">{{ totalRoofArea }}m²</span>
                </p>
                <p class="project-panel__field q-mb-sm">
                  <span class="project-panel__field--label">{{
                    $t("sidebar.numberOfPanels")
                  }}</span>
                  <span class="project-panel__field--value">{{
                    totalNumberOfPanels
                  }}</span>
                </p>
                <p class="project-panel__field q-mb-sm">
                  <span class="project-panel__field--label">{{
                    $t("sidebar.totalPanelsArea")
                  }}</span>
                  <span class="project-panel__field--value">{{ totalPanelsArea }}m²</span>
                </p>
              </div>
            </q-card-section>
          </q-card>
        </q-expansion-item>
        <q-expansion-item
          v-if="currentUser.isEmployee"
          v-model="orientationCardExpanded"
          :header-class="`summary__header summary__header--small ${
            orientationCardExpanded ? 'summary__header--expanded' : ''
          }`"
          class="summary__block"
          :label="$t('sidebar.modelOrientation')"
        >
          <q-card
            class="summary__card"
            :class="orientationCardExpanded && 'summary__card--expanded'"
          >
            <q-card-section>
              <div class="orientation-input-group q-mb-md">
                <p class="orientation-label">{{ $t("sidebar.xAxis") }}</p>
                <el-input-number
                  :model-value="project.orientation.x"
                  type="number"
                  class="orientation-input-field"
                  controls-position="right"
                  :precision="2"
                  :min="-180"
                  :max="180"
                  :value-on-clear="null"
                  @update:modelValue="
                    (value) => {
                      project.orientation.x = value;
                      orientationUpdated();
                    }
                  "
                >
                  <template #increase-icon>
                    <img src="@/assets/icons/arrow-up.svg" />
                  </template>
                  <template #decrease-icon>
                    <img src="@/assets/icons/arrow-down.svg" />
                  </template>
                </el-input-number>
              </div>

              <div class="orientation-input-group q-mb-md">
                <p class="orientation-label">{{ $t("sidebar.yAxis") }}</p>
                <el-input-number
                  :model-value="project.orientation.y"
                  type="number"
                  class="orientation-input-field"
                  controls-position="right"
                  :precision="2"
                  :min="-180"
                  :max="180"
                  :value-on-clear="null"
                  @update:modelValue="
                    (value) => {
                      project.orientation.y = value;
                      orientationUpdated();
                    }
                  "
                >
                  <template #increase-icon>
                    <img src="@/assets/icons/arrow-up.svg" />
                  </template>
                  <template #decrease-icon>
                    <img src="@/assets/icons/arrow-down.svg" />
                  </template>
                </el-input-number>
              </div>

              <div class="orientation-input-group q-mb-md">
                <p class="orientation-label">{{ $t("sidebar.zAxis") }}</p>
                <el-input-number
                  :model-value="project.orientation.z"
                  type="number"
                  class="orientation-input-field"
                  controls-position="right"
                  :precision="2"
                  :min="-180"
                  :max="180"
                  :value-on-clear="null"
                  @update:modelValue="
                    (value) => {
                      project.orientation.z = value;
                      orientationUpdated();
                    }
                  "
                >
                  <template #increase-icon>
                    <img src="@/assets/icons/arrow-up.svg" />
                  </template>
                  <template #decrease-icon>
                    <img src="@/assets/icons/arrow-down.svg" />
                  </template>
                </el-input-number>
              </div>

              <q-btn
                no-caps
                color="primary"
                class="add-panels q-mt-sm"
                @click="saveOrientation"
                >{{ $t("sidebar.save") }}
              </q-btn>

              <div class="q-mt-sm">
                <span class="reset-orientation-link" @click="resetOrientation">
                  {{ $t("sidebar.resetAll") }}
                </span>
              </div>
            </q-card-section>
          </q-card>
        </q-expansion-item>
        <q-expansion-item
          v-if="currentUser.isEmployee"
          v-model="scaleCardExpanded"
          :header-class="`summary__header summary__header--small ${
            scaleCardExpanded ? 'summary__header--expanded' : ''
          }`"
          class="summary__block"
          :label="$t('sidebar.scaleModel')"
          style="color: black"
        >
          <q-card
            class="summary__card"
            :class="scaleCardExpanded && 'summary__card--expanded'"
          >
            <q-card-section>
              <div>
                <input
                  type="number"
                  class="scale-input-field"
                  :value="project.scale"
                  step="0.1"
                  min="0.1"
                  max="10"
                  @input="
                    (event) => {
                      scalingUpdated(parseFloat(event.target.value));
                    }
                  "
                />
              </div>
            </q-card-section>
          </q-card>
        </q-expansion-item>

        <hr class="project-panel__line" />

        <MainSidebar
          v-if="project.id && !anonymousUser"
          ref="mainSidebaRef"
          :sample="sample"
          :solarGroupDetails="areas"
          :distancesDetails="measurements"
          :areaDetails="measurementAreas"
          :completedAreas="completedAreas"
          :hideAreas="hideAreas"
          :showAreas="showAreas"
          :measurementAreaCreated="measurementAreaCreated"
          :showSingleArea="showSingleArea"
          :hideSingleArea="hideSingleArea"
          :hideAllMeasurementAreas="hideAllMeasurementAreas"
          :showAllMeasurementAreas="showAllMeasurementAreas"
          :hideMeasurements="hideMeasurements"
          :showMeasurements="showMeasurements"
          @updateSelectedArea="updateSelectedArea"
          @updateSelectedDistance="updateSelectedDistance"
          :projectId="project.id"
          :objectIsloaded="objectIsloaded"
          :deleteMeasurement="deleteMeasurement"
          :selectedFromSidebarEvent="selectedFromSidebarEvent"
          :removeSelectedMeasurementArea="removeSelectedMeasurementArea"
          :removeSolarGroup="removeSolarGroup"
          :detectedMeasurementArea="detectedMeasurementArea"
          :unselectFromSidebar="unselectFromSidebar"
          :unselectSolarArea="unselectSolarArea"
          :anonymousUser="anonymousUser"
          :active="active"
          @updateActive="updateActive"
          :selectedMeasurementArea="selectedMeasurementArea"
          :unselectMeasurementArea="unselectMeasurementArea"
          :getAreaPanelsCount="getAreaPanelsCount"
          :calculateVisiblePanelsArea="calculateVisiblePanelsArea"
          :calculateSurfaceArea="calculateSurfaceArea"
          :removeSelectedArea="removeSelectedArea"
          :fromDraggStart="fromDraggStart"
        />
        <hr v-if="currentUser.isEmployee" class="project-panel__line" />
        <hr v-if="currentUser.isEmployee" class="project-panel__line" />
        <q-btn
          no-caps
          style="background: var(--color-gree300); color: var(--color-black)"
          class="report-button q-mt-sm"
          :disabled="totalNumberOfPanels === 0 || !solarSectionVisible"
          @click="printPage"
          >{{ $t("sidebar.createReport") }}

          <q-tooltip class="report-tooltip" v-if="totalNumberOfPanels === 0">
            {{ $t("sidebar.createReportInfo") }}
          </q-tooltip>
        </q-btn>
        <ExcelExport
          v-if="!anonymousUser"
          :project-id="project.id"
          :measurement-areas="measurementAreas"
          :measurements="measurements"
          :areas="areas"
          :panel-types="[...verticalPanelTypes, ...horizontalPanelTypes, ...customPanels]"
          :current-user="currentUser"
        />
      </div>
    </div>
    <div
      id="airteam-viewer"
      ref="airteamViewerDiv"
      class="airteam-viewer"
      :class="
        sample && !project.isSampleProject
          ? 'col-100'
          : sidebarOpen
          ? 'col-lg-10 col-md-9 col-sm-8 hide-mobile'
          : 'col-115'
      "
    >
      <div
        v-show="active !== 2 && active !== 3 && active !== 6"
        class="position0 q-pa-sm q-ml-sm hide-mobile"
      >
        <div
          class="undo-redo q-mx-sm"
          :class="undoStack.length === 0 ? 'cursor-disabled' : 'cursor-pointer'"
          @click="undo"
        >
          <img
            v-show="!undoLoading"
            class="undo-arrow"
            src="@/assets/icons/left-arrow.svg"
          />
          <img
            v-show="undoLoading"
            class="undo-arrow"
            src="@/assets/icons/spinner-loading.svg"
          />
        </div>
        <div
          class="undo-redo q-mx-sm"
          :class="redoStack.length === 0 ? 'cursor-disabled' : 'cursor-pointer'"
          @click="redo"
        >
          <img
            v-show="!redoLoading"
            class="undo-arrow"
            src="@/assets/icons/right-arrow.svg"
          />
          <img
            v-show="redoLoading"
            class="undo-arrow"
            src="@/assets/icons/spinner-loading.svg"
          />
        </div>
      </div>
      <div class="position1" id="right-side-bar">
        <div v-if="!sample || project.isSampleProject" class="screenshot-bg">
          <span
            class="q-mr-sm hide-mobile"
            :class="active !== 0 ? 'cursor-disabled' : 'cursor-pointer'"
          >
            <img
              style="padding-top: 10px"
              src="@/assets/icons/screenshot-button.svg"
              @click="takeScreenshot"
            />
          </span>

          <span v-if="!anonymousUser" class="q-mr-xs">
            <span v-if="!isBobbieContext" class="icon">
              <img
                v-if="$i18n.locale === 'de'"
                src="@/assets/icons/share-button-de.svg"
                @click="shareSolarModel"
              />
              <img
                v-else
                src="@/assets/icons/share-button.svg"
                @click="shareSolarModel"
              />
            </span>
            <span v-else class="icon">
              <img
                v-if="$i18n.locale === 'de'"
                src="@/assets/icons/share-button-de-blue.svg"
                @click="shareSolarModel"
              />
              <img
                v-else
                src="@/assets/icons/share-button-blue.svg"
                @click="shareSolarModel"
              />
            </span>
          </span>
        </div>

        <SolarSidebar
          v-if="selectedArea && selectedArea.type === 'SOLAR_GROUP'"
          :areas="areas"
          :horizontalPanels="horizontalPanelTypes"
          :verticalPanels="verticalPanelTypes"
          :activeUserId="currentUser.id"
          :selectedSolarGroup="selectedArea"
          :sample="sample"
          :populateArea="populateAreaDebounced"
          :createUpdateSolarGroup="createUpdateSolarGroup"
          :removeSolarGroupPanels="removeSolarGroupPanels"
          :addOffsetToPanels="addOffsetToPanels"
          :removeSolarGroup="removeSolarGroup"
          :anonymousUser="anonymousUser"
          :likedPanel="likedPanel"
          :likedPanelId="likedPanelId"
          :starClicked="starClicked"
          :disableRestrictedAreaMode="disableRestrictedAreaMode"
          :disableIndividualPlacementMode="disableIndividualPlacementMode"
          :unlikePanel="unlikePanel"
          :loadCustomPanelTextures="loadCustomPanelTextures"
          :getUserDefaultSolarValues="getUserDefaultSolarValues"
          :active="active"
          :individualPanelOrientation="individualPanelOrientation"
          :enableIndividualPlacementMode="enableIndividualPlacementMode"
          :validateIndividualPanels="validateIndividualPanels"
          :changePanelTypeForIndividualPanels="changePanelTypeForIndividualPanels"
          :solarGroupIndex="selectedSolarGroupIndex"
          @setupCompleted="unselectSolarArea"
          @restrictedAreasPlacement="() => toggleActive(6)"
          @solarGroupDeleted="unselectSolarArea"
          @defaultValuesUpdates="resyncDefaultValuesOnUpdate"
          @toggle-hotkeys="toggleHotkeys"
        />
        <GalleryWrapper
          :active="active"
          :projectId="project.id"
          :technicalCategoryId="project.technicalCategoryId"
          :bestImage="bestImage"
          :toggleActive="toggleActive"
          :initializeGalleryMode="initializeGalleryMode"
          :toggleFullScreenImageActive="toggleFullScreenImageActive"
          :fullScreenImageActive="fullScreenImageActive"
          :bestImagesPoints="bestImagesPoints"
          :mousePositionOnScreen="mousePositionOnScreen"
          :calculateOffsetForFullScreen="calculateOffsetForFullScreen"
          :originalImageWidth="originalImageWidth"
          :originalImageHeight="originalImageHeight"
          :changeCursorToLoading="changeCursorToLoading"
          :thumbnailsAvailable="thumbnailsAvailable"
          :setThumbnailsAvailable="setThumbnailsAvailable"
          :isEmployee="currentUser.isEmployee"
          :galleryWarningShown="galleryWarningShown"
          :usingBlurredImages="usingBlurredImages"
          :fetchCamerasWithImages="fetchCamerasWithImages"
          @close="() => toggleActive(0)"
          @openCsvVisualizer="openCsvVisualizer"
          :camerasDisplayed="camerasDisplayed"
          @toggleCameras="camerasDisplayed ? changeCamerasVisibility(false) : displayCameras()"
        />
        <CsvVisualizer
          v-if="csvVisualizerVisible"
          :camerasCsv="camerasCsv"
          :imagesCountRc="csvVisualizerStats.imagesCountRc"
          :imagesCountNonRc="csvVisualizerStats.imagesCountNonRc"
          :useRcFlag="csvVisualizerStats.useRcFlag"
          :technicalCategoryId="csvVisualizerStats.technicalCategoryId"
          :projectId="project.id"
          @close="csvVisualizerVisible = false"
        />
      </div>
      <div v-if="company.logo && anonymousUser" class="position2">
        <span>
          <img style="padding-top: 10px" :src="company.logo" width="100" />
        </span>
      </div>
      <div
        v-show="!fullScreenImageActive && !csvVisualizerVisible"
        id="panels-toolbar"
        class="position5 hide-mobile"
        :class="showDownloadAllDialog ? 'position5--disabled' : ''"
      >
        <div class="panels__toolbar main-menu">
          <div
            class="text-center panels__toolbar-text cursor-pointer q-py-sm"
            @click="(e) => toggleActive(0)"
          >
            <CursorIcon
              class="menu-icon"
              :color="active === 0 ? activeColor : 'white'"
            />
          </div>
          <img src="@/assets/icons/menu-item-line.svg" />
          <div
            ref="measurementButton"
            class="text-center panels__toolbar-text cursor-pointer q-py-sm"
            @click="toggleActive(1)"
          >
            <MeasureIcon
              class="menu-icon"
              :color="measurementModeActive ? activeColor : 'white'"
            />
          </div>
          <img v-if="!anonymousUser" src="@/assets/icons/menu-item-line.svg" />
          <div
            v-if="!anonymousUser"
            class="text-center panels__toolbar-text cursor-pointer q-py-sm"
            @click="(e) => toggleActive(2)"
          >
            <SolarIcon
              class="menu-icon"
              :color="active === 2 || active === 3 || active === 6 ? activeColor : 'white'"
            />
          </div>
          <img
            v-if="!anonymousUser && (!sample || project.isSampleProject)"
            src="@/assets/icons/menu-item-line.svg"
          />
          <div
            v-if="!anonymousUser && (!sample || project.isSampleProject)"
            class="text-center panels__toolbar-text cursor-pointer q-py-sm"
            @click="handleGalleryClick"
          >
            <GalleryIcon
              class="menu-icon"
              :color="active === 5 ? activeColor : 'white'"
            />
          </div>
        </div>
      </div>
      <div
        v-if="measurementModeActive"
        class="measurement-menu hide-mobile"
        :style="measurementMenuPosition"
        @mouseover="measurementMenuExpanded = true"
        @mouseleave="showSmallMeasurementMenu"
      >
        <div class="panels__toolbar panels__toolbar--small main-menu--small">
          <div
            class="panels__toolbar-text panels__toolbar-text--small text-left q-mb-xs cursor-pointer"
            @click="(e) => toggleActive(1)"
          >
            <DistanceIcon
              class="menu-icon--small"
              :color="active === 1 ? activeColor : 'white'"
            />
            <span
              v-if="measurementMenuExpanded"
              class="q-ml-sm"
              :class="active === 1 ? 'menu-text--active' : ''"
              >{{ $t("modes.distance") }}</span
            >
          </div>
          <div
            class="panels__toolbar-text panels__toolbar-text--small text-left cursor-pointer"
            @click="(e) => toggleActive(4)"
          >
            <AreaIcon
              class="menu-icon--small"
              :color="active === 4 ? activeColor : 'white'"
            />
            <span
              v-if="measurementMenuExpanded"
              class="q-ml-sm"
              :class="active === 4 ? 'menu-text--active' : ''"
              >{{ $t("modes.area") }}</span
            >
          </div>
        </div>
      </div>
      <div
        class="position9 q-pa-sm"
        :class="sample && !project.isSampleProject ? 'position9--sample' : ''"
      >
        <span
          class="q-mx-xs cursor-pointer hide-mobile hide-iframe nav-button"
          :class="{ 'nav-button--active': navigationMenu === 0 }"
          style="padding-right: 2px; padding-top: 4px; padding-bottom: 4px;"
          @click="handleOrbitClick"
        >
          <OrbitIcon
            style="width: 30px; height: 30px; object-fit: contain"
            color="white"
          />
        </span>
        <img
          class="hide-mobile hide-iframe"
          style="margin-left: 10px; margin-right: 6px;"
          src="@/assets/icons/navigation-menu-break.svg"
        />
        <span
          class="q-ml-sm q-mr-md cursor-pointer hide-mobile hide-iframe nav-button"
          :class="{ 'nav-button--active': navigationMenu === 1 }"
          @click="handlePanClick"
        >
          <PanIcon
            style="width: 26px"
            color="white"
          />
        </span>
        <img
          class="hide-mobile hide-iframe"
          src="@/assets/icons/navigation-menu-break.svg"
        />
        <span
          v-if="currentUser.isEmployee"
          class="q-mx-sm cursor-pointer hide-mobile hide-iframe"
          @click="toggleCameraMode"
        >
          <img
            style="width: 45px"
            v-show="!isOrthographicCamera"
            src="@/assets/icons/camera-orthographic.svg"
          />
          <img
            style="width: 65px"
            v-show="isOrthographicCamera"
            src="@/assets/icons/camera-perspective.svg"
          />

        </span>
        <span
          v-if="!currentUser.isEmployee"
          class="q-mx-sm cursor-pointer"
          @click="resetCameraPosition"
        >
          <FullScreenNavIcon
            style="width: 30px"
            :color="showGreenFullScreen ? activeColor : 'white'"
          />
        </span>
        <span
          v-if="currentUser.isEmployee"
          class="view-plane-button cursor-pointer q-mx-xs"
          @click="
            viewPlane = 'XY';
            resetCameraPosition();
          "
        >
          XY
        </span>
        <span
          v-if="currentUser.isEmployee"
          class="view-plane-button cursor-pointer q-mx-xs"
          @click="
            viewPlane = 'YZ';
            resetCameraPosition();
          "
        >
          YZ
        </span>
        <span
          v-if="currentUser.isEmployee"
          class="view-plane-button cursor-pointer q-mx-xs"
          @click="
            viewPlane = 'XZ';
            resetCameraPosition();
          "
        >
          XZ
        </span>
        <img
          class="q-mx-md hide-mobile hide-iframe"
          src="@/assets/icons/navigation-menu-break.svg"
        />
        <span
          class="q-mx-md cursor-pointer hide-mobile hide-iframe"
          @click="openHelpModal"
        >
          <img class="help-button" src="@/assets/icons/help-icon.svg" alt="help" />
        </span>
      </div>
      <div class="position10">
        <img src="@/assets/model/metric_info.svg" class="q-mr-sm" />
        {{ $t("canvas.unitOfMeasurement") }}
      </div>
      <div id="arrow-container" class="position11"></div>
    </div>
    <AirteamViewerHelper :isOpen="openHelperModal" @helper-closed="closeHelperModal" />
  </div>
  <el-dialog
    v-model="dialogVisible"
    width="580"
    style="background-color: var(--color-dark-bg-primary); border-radius: 10px"
  >
    <template #header>
      <img
        v-if="!isBobbieContext"
        src="@/assets/icons/check-o.svg"
        alt="icon"
        class="check-icon"
        style="width: 20px; height: 20px; vertical-align: middle"
      />
      <img
        v-else
        src="@/assets/icons/check-o-blue.svg"
        alt="icon"
        class="check-icon"
        style="width: 20px; height: 20px; vertical-align: middle"
      />
      <span class="share-title">{{ $t("share.title") }}</span>
    </template>
    <div class="share-message-body">
      <div>{{ $t("share.message1") }}</div>
      <div>{{ $t("share.message2") }}</div>
      <div class="valid">{{ $t("share.valid") }}</div>
    </div>
    <template #footer>
      <div>
        <el-button class="share-close-button" type="text" @click="dialogVisible = false">
          {{ $t("share.close") }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import * as THREE from "three";
import { mapActions, mapGetters } from "vuex";
import { TransformControls } from "@/modules/TransformControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { ElMessage } from "element-plus";
import { ref, toRaw } from "vue";
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import AirteamViewerHelper from "@/components/AirteamViewerHelper.vue";
import { isProduction } from "@/utils/env.js";
import API from "@/api/API";
import { decodeJwt } from "@/utils/decode-jwt.js";
import { trackEvent } from "@/utils/analytics.js";
import CameraControls from "camera-controls";
import { H } from "highlight.run";
import * as areaMethods from "@/modules/area";
import * as restrictedAreaMethods from "@/modules/restrictedArea";
import * as undoRedoMethods from "@/modules/undoRedo";
import * as measurementMethods from "@/modules/measurement";
import * as areaMeasurementMethods from "@/modules/areaMeasurement";
import * as panelMethods from "@/modules/panel";
import * as commonMethods from "@/modules/common";
import * as authZitadelMethods from "@/services/authService.js";
import * as navigationMethods from "@/modules/navigation";
import * as cameraMethods from "@/modules/camera";
import * as galleryMethods from "@/modules/gallery";
import { initializeModes } from "@/modules/modes.js";
import { changeLocale } from "@/plugins/i18n";
import {
  storeLanguagePreference,
  getStoredLanguagePreference,
} from "@/utils/language-persistence";
import { getGPUTier } from "detect-gpu";
import {
  WHITE,
  VERTICAL,
  HORIZONTAL,
  TEST_COMPANIES,
  TECHNICAL_PICTURES_CATEGORY,
  BLURRED_TECHNICAL_PICTURES_CATEGORY,
} from "@/constants";
// removed due to mk3 split release
import MainSidebar from "@/components/NewSolarSidebar/MainSidebar.vue";
import SolarSidebar from "@/components/RightSidebar/SolarSidebar.vue";
import GalleryWrapper from "@/components/Gallery/GalleryWrapper.vue";
import CsvVisualizer from "@/components/Gallery/CsvVisualizer.vue";
import BreadCrumbs from "@/components/NewSolarSidebar/BreadCrumbs.vue";
import newMarker from "@/assets/model/new_marker_3.svg";
import Stats from "stats.js";
import ExcelExport from "@/components/NewSolarSidebar/ExcelExport.vue"; // Import stats.js
import { getCookie } from "../utils/cookie-utils";
import { BLURRED_CATEGORY_INDEX } from "../constants";
import { bobbieTheme } from "@/scss/_theme-bobbie.js";
import { theme as defaultTheme } from "@/scss/_theme.js";
import { initViewerColors } from "@/utils/viewer-colors";
import CursorIcon from "@/components/icons/CursorIcon.vue";
import MeasureIcon from "@/components/icons/MeasureIcon.vue";
import SolarIcon from "@/components/icons/SolarIcon.vue";
import GalleryIcon from "@/components/icons/GalleryIcon.vue";
import DistanceIcon from "@/components/icons/DistanceIcon.vue";
import AreaIcon from "@/components/icons/AreaIcon.vue";
import FullScreenNavIcon from "@/components/icons/FullScreenNavIcon.vue";
import LocationIcon from "@/components/icons/LocationIcon.vue";
import PanIcon from "@/components/icons/PanIcon.vue";
import OrbitIcon from "@/components/icons/OrbitIcon.vue";
CameraControls.install({ THREE: THREE });

export default {
  name: "AirteamViewer",
  components: {
    ExcelExport,
    AirteamViewerHelper,
    MainSidebar,
    SolarSidebar,
    BreadCrumbs,
    CsvVisualizer,
    GalleryWrapper,
    CursorIcon,
    MeasureIcon,
    SolarIcon,
    GalleryIcon,
    DistanceIcon,
    AreaIcon,
    FullScreenNavIcon,
    LocationIcon,
    PanIcon,
    OrbitIcon,
  },
  props: ["isSample", "thermal"],
  data() {
    return {
      sample: this.isSample || false,
      // Keep for compatibility with helper functions
      projectId: null,
      companyId: null,
      // -----------

      project: {
        id: null,
        modelId: null,
        modelPath: null,
        flightDate: null,
        address: null,
        orientation: {
          x: -90,
          y: 0,
          z: 0,
        },
        scale: 1.0,
        technicalCategoryId: null,
      },
      savedOrientation: {
        x: -90,
        y: 0,
        z: 0,
      },
      company: {
        id: null,
        logo: null,
      },
      currentUser: {
        id: null,
        isEmployee: null,
      },

      pinpointMaterial: null,
      mousePositionOnScreen: {
        x: 0,
        y: 0,
      },
      bestImagesPoints: [],
      fromDraggStart: false,
      temporaryMeasurement: null,
      selectedMeasurement: null,
      bestImage: null,
      detectedMeasurementArea: null,
      isMeasurementSelected: false,
      previousSolarArea: null,
      stencilCount: 1,
      measurementDrag: false,
      objectIsloaded: null,
      measurementAreaCreated: null,
      likedPanel: null,
      likedPanelId: null,
      cameraObject: null,
      cameraControls: null,
      perspectiveCamera: null,
      orthographicCamera: null,
      transformControls: null,
      infiniteGridHelper: null,
      clock: null,
      zoomCenterDot: null,
      lightning: false,
      container: null,
      controls: null,
      renderer: null,
      labelRenderer: null,
      lighting: null,
      ambient: null,
      keyLight: null,
      fillLight: null,
      backLight: null,
      active: 0,
      mouse: null,
      spriteMaterial: null,
      dragOn: false,
      areas: [],
      measurementAreas: [],
      sidebarOpen: window.innerWidth > 600,
      areaExpanded: true,
      cameraMesh: null,
      panelAdditionType: null,
      panelType: "",
      panelSpacing: null,
      selectedArea: null,
      panelStep: 0,
      panelSetupConfirmed: false,
      pmremGenerator: null,
      existingAreasIds: [],
      existingMeasurementsIds: [],
      neutralEnvironment: null,
      callCount: 0,
      movingLeft: true,
      isEditButtonDisplayed: false,
      pointSelected: null,
      screenshot: null,
      isCtrlDown: false,
      isAltDown: false,
      isShiftDown: false,
      combinedMatrix: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      selectionBox: null,
      positions: {
        clientX: undefined,
        clientY: undefined,
        movementX: 0,
        movementY: 0,
      },
      measurementLine: null,
      currentMeasurementAreaLines: [],
      areaEndingLine: null,
      selectedMeasurementArea: null,
      panelAlignment: false,
      panelCounter: 1,
      openHelperModal: false,
      texturePath: "/assets/textures/",
      texturesLoading: true,
      trashSize: {
        width: 0.5,
        height: 0.5,
      },
      verticalPanelTypes: [],
      horizontalPanelTypes: [],
      customSizePanel: {
        width: null,
        height: null,
      },
      undoStack: [],
      redoStack: [],
      currentMeasurementLines: [],
      orientationCardExpanded: false,
      summaryExpanded: false,
      scaleCardExpanded: false,
      isOrthographicCamera: false,
      rotationSpeed: 0.001,
      upperAngleRange: 0.7854, // 45 degrees in radians
      lowerAngleRange: -0.7854,
      anonymousUser: false,
      sessionStartTime: null,
      measurements: [],
      mouseOverModel: false,
      canvasElementRef: null,
      navigationMenu: null,
      showGreenFullScreen: false,
      panelsToUpdate: [],
      measurementModeActive: false,
      measurementMenuExpanded: false,
      dotGeometry: null,
      middotGeometry: null,
      timestamps: [],
      currentPixelRatio: null,
      loading: false,
      loadingProgress: 0,
      annotationRenderOrder: 0,
      undoLoading: false,
      redoLoading: false,
      solarDefaultValues: null,
      exportedManually: false,
      stats: null,
      individualPanelOrientation: null,
      lastUpdatedTime: 0,
      throttleInterval: 15,
      dialogVisible: ref(false),
      camerasCsv: [],
      camerasFrustums: [],
      galleryPoint: null,
      sampleModelName: null,
      fullScreenImageActive: false,
      originalImageWidth: null,
      originalImageHeight: null,
      cameraPositionsCsvUrl: null,
      cameraWorkers: [],
      workerInitialized: null,
      workerResults: null,
      completedWorkers: 0,
      totalWorkers: 0,
      isProcessingLineOfSight: false,
      csvAvailable: true,
      camerasLoadedWithImages: false,
      galleryWarningShown: false,
      thumbnailsAvailable: null,
      galleryPointsSelected: 0,
      pendingLineRaycastId: null,
      populateAreaTimeout: null,
      populateAreaPending: false,
      populateAreaDelay: 200,
      csvVisualizerVisible: false,
      csvVisualizerStats: {
        imagesCountRc: 0,
        imagesCountNonRc: 0,
        useRcFlag: true,
        technicalCategoryId: null,
      },
      viewPlane: "XY",
      viewPlaneDropdownOpen: false,
      usingBlurredImages: false,
    };
  },
  watch: {
    panelSpacing() {
      if (this.panelSpacing?.includes(",")) {
        this.panelSpacing = this.panelSpacing.replace(/,/g, ".");
      }
    },
    orientationCardExpanded(isExpanded) {
      if (isExpanded) {
        this.enableTransformControls();
      } else {
        this.disableTransformControls();
        if (this.isOrthographicCamera) {
          this.toggleCameraMode();
        }
      }
    },
  },
  computed: {
    ...mapGetters(["customPanels"]),
    isBobbieContext() {
      return this.$store.state.auth?.isBobbieContext || false;
    },
    activeColor() {
      return this.isBobbieContext ? "#2EAFFF" : "#23E7A5";
    },
    sections() {
      return this.$store.state.section;
    },
    solarSectionVisible() {
      return this.sections.config.solar_groups_visible;
    },
    modes() {
      return initializeModes(this);
    },
    restrictedAreaMode() {
      const mode = this.modes.find((mode) => mode.name === "RESTRICTED_AREA");
      return mode.enabled;
    },
    getViewerWidth() {
      if (this.sample && !this.project.isSampleProject) return 1.0;
      if (this.sidebarOpen) return 0.83333;
      return 0.98;
    },
    completedAreas() {
      return this.areas.filter((area) => area.plane);
    },
    chosenPanel() {
      return this.panelTypes.find((item) => item.name === this.panelType);
    },
    totalNumberOfPanels() {
      return this.areas
        .filter((area) => (area.plane || area.instancedMesh) && area.show !== false)
        .map((area) => this.getAreaPanelsCount(area))
        .reduce((a, b) => a + b, 0);
    },
    totalRoofArea() {
      let totalArea = 0;
      const visibleAreas = this.areas.filter((area) => area.show !== false);
      for (let area of visibleAreas) {
        totalArea += Number(this.calculateSurfaceArea(area));
      }
      return totalArea.toFixed(2);
    },
    totalPanelsArea() {
      let totalArea = 0;
      const visibleAreas = this.areas.filter((area) => area.plane && area.show !== false);

      for (let area of visibleAreas) {
        totalArea += this.calculateVisiblePanelsArea(area);
      }
      return totalArea.toFixed(2);
    },
    panelsDataPerSize() {
      const sizeToPanelNumber = {};
      const sizeToPanelSurface = {};
      for (const i in this.areas) {
        for (const j in this.areas[i].panels) {
          const panel = this.areas[i].panels[j];
          const size = panel.size.width + " x " + panel.size.height + " mm";
          if (size in sizeToPanelSurface) {
            sizeToPanelNumber[size] += 1;
            sizeToPanelSurface[size] += panel.size.height * panel.size.width;
          } else {
            sizeToPanelNumber[size] = 1;
            sizeToPanelSurface[size] = panel.size.height * panel.size.width;
          }
        }
      }
      return [sizeToPanelNumber, sizeToPanelSurface];
    },
    panelTypes() {
      const panelsList = this.panelAlignment
        ? this.horizontalPanelTypes
        : this.verticalPanelTypes;

      const filteredCustomPanels = this.customPanels.filter((panel) => {
        if (panel.show && (panel.companyId || panel.userId === this.currentUser.id)) {
          return this.panelAlignment
            ? panel.orientation === HORIZONTAL
            : panel.orientation === VERTICAL;
        }
      });
      return [...filteredCustomPanels, ...panelsList];
    },
    isFirstPoint() {
      if (this.areas.length === 0 || this.areas[this.areas.length - 1].closed)
        return true;
      return false;
    },
    isFirstMeasurementPoint() {
      if (
        this.measurementAreas.length === 0 ||
        this.measurementAreas[this.measurementAreas.length - 1].closed
      )
        return true;
      return false;
    },
    modelRotation() {
      return this.modelObject.children[0].rotation;
    },
    currentLeftClickAction() {
      if (this.active === 0) return CameraControls.ACTION.ROTATE;
      return CameraControls.ACTION.NONE;
    },
    camerasDisplayed() {
      return this.cameraMesh?.visible;
    },
    selectedSolarGroupIndex() {
      const closedAreas = this.areas.filter((area) => area.closed);
      return closedAreas.findIndex((area) => area.id === this.selectedArea?.id) + 1;
    },
    measurementMenuPosition() {
      if (!this.$refs.measurementButton) {
        return { top: "40vh", left: "60px" }; // fallback to original values
      }

      const buttonRect = this.$refs.measurementButton.$el || this.$refs.measurementButton;
      if (!buttonRect || !buttonRect.getBoundingClientRect) {
        return { top: "40vh", left: "60px" }; // fallback to original values
      }

      const rect = buttonRect.getBoundingClientRect();
      const top = rect.top + rect.height / 2;

      return {
        top: `${top}px`,
        left: "60px", // keep original left positioning
        transform: "translateY(-50%)",
      };
    },
  },
  async created() {
    initViewerColors(this.isBobbieContext ? bobbieTheme : defaultTheme);

    let lang = this.$route.query.lang;

    // Store language preference in localStorage for persistence through auth flow
    if (lang) {
      storeLanguagePreference(lang);
    } else {
      // If no lang parameter in URL, try to get from localStorage
      lang = getStoredLanguagePreference();
    }

    await changeLocale(lang);
    this.loading = true;
    this.$store.commit("setIsThermal", this.thermal ? true : false);
    this.sessionStartTime = new Date();

    if (this.sample) {
      this.sampleModelName = this.$route.path.substring(1);
    }

    const gpuTier = await getGPUTier();

    await this.setUpProjectAndCompany();

    trackEvent("fast_fusion_solar_entered", {
      page_title: "AirteamViewer",
      page_location: window.location.href,
      page_path: window.location.pathname,
      project_id: this.project.id,
      user: this.activeUser?.email,
      date: Date.now(),
      customer_type: this.currentUser.isEmployee ? "employee" : "customer",
      gpu_tier: gpuTier.tier,
      gpu_model: gpuTier.gpu,
    });

    this.getCustomPanels({ projectId: this.project.id });
    if (!TEST_COMPANIES.includes(this.company.id)) {
      trackEvent("fast_fusion_solar_entered", {
        page_title: "AirteamViewer",
        page_location: window.location.href,
        page_path: window.location.pathname,
        project_id: this.project.id,
        user: this.activeUser?.email,
        date: Date.now(),
        customer_type: this.currentUser.isEmployee ? "employee" : "customer",
      });
    }

    this.init();
    this.showBridge(this.project.modelId, this.project.modelPath);
    const clock = new THREE.Clock();
    this.clock = clock;
  },
  mounted() {
    document.querySelector("body").style.paddingTop = "0";
    // Add check for iframe and apply class
    if (window.self !== window.top) {
      document.body.classList.add("within-iframe");
    }

    window.addEventListener("pageshow", this.handlePageShow);
  },
  beforeUnmount() {
    document.querySelector("body").style.paddingTop = null;
    window.removeEventListener("pageshow", this.handlePageShow);
  },
  beforeRouteLeave(to, from, next) {
    this.trackPageView();

    document.removeEventListener("keydown", this.keyDownInit);
    document.removeEventListener("keyup", this.keyUpInit);
    window.removeEventListener("keydown", this.keyDownInitWindow);
    window.removeEventListener("dblclick", this.addZoomCenter, false);
    window.removeEventListener("resize", this.onWindowResize, false);
    window.removeEventListener("blur", this.resetCommandKeyStates, false);

    if (this.active === 0) {
      document.removeEventListener(
        "mousemove",
        this.detectMeasurementIntersection,
        false
      );
    } else if (this.active === 1) {
      document.removeEventListener("click", this.addMarker, false);
      document.removeEventListener("mousemove", this.detectModelIntersection, false);
      document.removeEventListener("keydown", this.deleteSelectedPanel, false);
    } else {
      document.removeEventListener("click", this.addPoint, false);
    }
    // TODO: closeEditMode method doesn't exist, commenting out to fix navigation
    //   this.closeEditMode();
    this.disablePointDragMode();
    next();
  },
  methods: {
    ...mapActions(["getCustomPanels", "setNewCustomPanels"]),
    ...Object.fromEntries(
      [
        ...Object.entries(commonMethods),
        ...Object.entries(navigationMethods),
        ...Object.entries(cameraMethods),
        ...Object.entries(measurementMethods),
        ...Object.entries(areaMethods),
        ...Object.entries(restrictedAreaMethods),
        ...Object.entries(panelMethods),
        ...Object.entries(areaMeasurementMethods),
        ...Object.entries(galleryMethods),
        ...Object.entries(undoRedoMethods),
      ].map(([key, value]) => [
        key,
        function (...args) {
          return value.apply(this, args);
        },
      ])
    ),
    openCsvVisualizer(stats) {
      this.csvVisualizerStats = stats;
      this.csvVisualizerVisible = true;
    },
    getRealProjectId(projectId) {
      if (isProduction) {
        if (projectId === "sample_1") return "85817";
        if (projectId === "sample_2") return "85571";
        if (projectId === "sample_3") return "86503";
      } else {
        if (projectId === "sample_1") return "6485";
      }
      return projectId;
    },
    checkIfSharedProjectIsSample() {
      if (isProduction) {
        switch (this.project.id.toString()) {
          case "85817":
            this.project.name = this.$t("sampleProjects.name", { id: 1 });
            this.project.description = this.$t("sampleProjects.description_1");
            this.project.isSample = true;
            break;

          case "85571":
            this.project.name = this.$t("sampleProjects.name", { id: 2 });
            this.project.description = this.$t("sampleProjects.description_2");
            this.project.isSample = true;
            break;

          case "86503":
            this.project.name = this.$t("sampleProjects.name", { id: 3 });
            this.project.description = this.$t("sampleProjects.description_3");
            this.project.isSample = true;
            break;
          default:
            this.project.name = this.project.id;
            this.project.isSample = false;
        }
      } else {
        switch (this.project.id.toString()) {
          case "6485":
            this.project.name = this.$t("sampleProjects.name", { id: 1 });
            this.project.description = this.$t("sampleProjects.description_1");
            this.project.isSample = true;
            break;
          default:
            this.project.name = this.project.id;
            this.project.isSample = false;
        }
      }
    },
    async setUpProjectAndCompany() {
      const token = this.$route?.query?.token;
      if (token) {
        const decodedPayload = decodeJwt(token);
        if (!decodedPayload) {
          authZitadelMethods.noCurrentProject();
          return;
        }
        const glbUrl = decodedPayload["project"]["modelPath"];
        const modelId = glbUrl.split("/").pop();
        const modelPath = glbUrl.replace(modelId, "");
        this.project = {
          ...decodedPayload["project"],
          modelId,
          modelPath,
          orientation: this.transformOrientation(
            decodedPayload["project"]["orientation"]
          ),
        };
        this.checkIfSharedProjectIsSample();
        this.company = decodedPayload["company"];
        this.hasCalibration = decodedPayload["project"]["orientation"] ? true : false;
        this.anonymousUser = true;

        if (decodedPayload["project"]["is_bobbie"]) {
          this.$store.commit("setIsBobbieContext", true);
          document.body.classList.add("bobbie-theme");

          const root = document.documentElement;
          root.style.setProperty("--q-primary", bobbieTheme.primary);
          root.style.setProperty("--q-secondary", bobbieTheme.secondary);
          root.style.setProperty("--q-accent", bobbieTheme.accent);
          root.style.setProperty("--q-dark", bobbieTheme.dark);
          root.style.setProperty("--q-positive", bobbieTheme.positive);
          root.style.setProperty("--q-negative", bobbieTheme.negative);
          root.style.setProperty("--q-info", bobbieTheme.info);
          root.style.setProperty("--q-warning", bobbieTheme.warning);

          document.title = "Bobbie 3D Modell";

          const favicon =
            document.querySelector("link[rel~='icon']") || document.createElement("link");
          favicon.rel = "icon";
          favicon.href = "/assets/favicon-bobbie.ico";
          document.head.appendChild(favicon);

          initViewerColors(bobbieTheme);
        }
      } else {
        if (!this.$route.params.projectId && !this.sample) {
          authZitadelMethods.noCurrentProject();
          return;
        }

        if (!this.sample) {
          const [
            projectResponse,
            projectCategoriesResponse,
            userResponse,
          ] = await Promise.all([
            API.projects.getProject(this.getRealProjectId(this.$route.params.projectId)),
            API.projectFileCategories.getProjectFileCategories(
              this.getRealProjectId(this.$route.params.projectId)
            ),
            API.users.getCurrentUser(),
          ]);
          const projectData = projectResponse.data;
          const userData = userResponse.data;
          let technicalCategory = projectCategoriesResponse.data.find(
            (category) =>
              category.nameEn === TECHNICAL_PICTURES_CATEGORY.en ||
              category.nameDe === TECHNICAL_PICTURES_CATEGORY.de ||
              category.nameEn === BLURRED_TECHNICAL_PICTURES_CATEGORY.en ||
              category.nameDe === BLURRED_TECHNICAL_PICTURES_CATEGORY.de
          );

          // Check both blurringEnabled attribute
          if (projectData.blurringEnabled) {
            technicalCategory = projectCategoriesResponse.data.find(
              (category) => category.index === BLURRED_CATEGORY_INDEX
            );
            this.usingBlurredImages = true;
          }

          const glbUrl = projectData.fastFusionModel.glb;
          const modelId = glbUrl.split("/").pop();
          const modelPath = glbUrl.replace(modelId, "");

          this.project = {
            id: projectData.pk,
            modelId: modelId,
            modelPath: modelPath,
            flightDate: projectData.flightDate,
            address: projectData.address,
            orientation: this.transformOrientation(projectData.solarModelOrientation),
            scale: projectData.solarModelScale,
            technicalCategoryId: technicalCategory?.pk || null,
            isSampleProject: !userData.isEmployee && projectData.isSample,
            isSample: projectData.isSample,
          };

          this.checkIfSharedProjectIsSample();

          this.company = {
            id: projectData.company.id,
            logo: projectData.company.logoViewer,
          };

          this.currentUser = {
            id: userData.userPk,
            isEmployee: userData.isEmployee,
          };
          this.$store.commit("setIsEmployee", userData.isEmployee);

          if (this.project.isSampleProject) this.sample = true;

          H.identify(this.project.id, { ...this.project });
        }
        this.projectId = this.project.id;
        this.companyId = this.company.id;
      }
    },

    transformOrientation(orientation) {
      let defaultOrientation = {
        x: -90,
        y: 0,
        z: 0,
      };
      try {
        // Handle both string and object formats
        if (typeof orientation === "string") {
          orientation = JSON.parse(orientation.replace(/'/g, '"'));
        }
        this.hasCalibration = true;
        // Use parseFloat to preserve decimal precision
        const transformedOrientation = {
          x: parseFloat(orientation.x || 0),
          y: parseFloat(orientation.y || 0),
          z: parseFloat(orientation.z || 0),
        };

        this.savedOrientation = { ...transformedOrientation };
        return transformedOrientation;
      } catch (error) {
        console.log("Orientation parsing failed", error);
        // Set default values when parsing fails
        this.hasCalibration = false;
        this.savedOrientation = { ...defaultOrientation };
        return defaultOrientation;
      }
    },

    updateActive(active) {
      this.toggleActive(active);
    },
    distanceMeasurementActivate() {
      if (!this.anonymousUser && (!this.sample || this.project.isSampleProject)) {
        this.$refs.mainSidebaRef.handleComponentCreation("distance");
      }
    },
    deselectSidebarDistances() {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.deselectSidebarDistances();
      }
    },
    colapseComponentsFrom(componentName) {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.setColapsedSections(componentName);
      }
    },
    deselectSidebarArea() {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.deselectSidebarArea();
      }
    },
    selectMeasurementFromCanvas(indexOfDistance) {
      if (!this.anonymousUser && (!this.sample || this.project.isSampleProject)) {
        this.$refs.mainSidebaRef.selectMeasurementFromCanvas(indexOfDistance);
      }
    },
    selectMeasurementAreaFromCanvas(indexOfArea) {
      if (!this.anonymousUser && (!this.sample || this.project.isSampleProject)) {
        this.$refs.mainSidebaRef.selectMeasurementAreaFromCanvas(indexOfArea);
      }
    },
    selectAreaOnSidebar(selectedSolarArea) {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.selectAreaOnSidebar(selectedSolarArea);
      }
    },
    unselectSolarAreaOnSidebar() {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.unselectSolarAreaOnSidebar();
      }
    },
    async starClicked(panel) {
      this.likePanel(panel);
    },
    solarGroupActivate() {
      this.$refs.mainSidebaRef.handleComponentCreation("solar");
    },
    areaMeasurementActivate() {
      if (!this.anonymousUser && (!this.sample || this.project.isSampleProject)) {
        this.$refs.mainSidebaRef.handleComponentCreation("area");
      }
    },
    distanceMeasurementActivate() {
      if (!this.anonymousUser && (!this.sample || this.project.isSampleProject)) {
        this.$refs.mainSidebaRef.handleComponentCreation("distance");
      }
    },
    unselectDistances() {
      this.hideMeasurementDetails();
      this.isMeasurementSelected = false;
      this.deselectSidebarDistances();
    },
    unselectArea() {
      this.unselectMeasurementArea(this.selectedMeasurementArea);
      this.selectedMeasurementArea = null;
      this.detectedMeasurementArea = null;
      this.deselectSidebarArea();
    },
    handleNavigateToOldPlacements() {
      let url = new URL(window.location.href);
      url.hostname = url.hostname.replace("beta.", "");
      window.location.href = url.toString();
    },
    starClicked(panel) {
      this.likePanel(panel);
    },
    async resyncDefaultValuesOnUpdate(updatedDefaultValues) {
      this.solarDefaultValues = updatedDefaultValues;
    },
    async likePanel(panel) {
      this.likedPanel = panel;
      try {
        await API.airteam3DViewer.updateLikedPanel({
          panelId: panel.id,
        });
      } catch (error) {
        console.error("Failed to update custom panel:", error);
      }
    },
    async unlikePanel() {
      this.likedPanel = null;
      this.likedPanelId = null;
      try {
        await API.airteam3DViewer.unlikePanel();
      } catch (error) {
        console.error("Failed to update custom panel:", error);
      }
    },
    updateSelectedArea(area) {
      if (this.active === 2) this.removeUnfinshedArea();
      this.updateActive(2);
      this.selectAreaWithoutClick(area);
    },
    updateSelectedDistance(distanceId) {
      if (this.active !== 0 && this.active !== 1 && this.active !== 4) {
        this.toggleActive(0);
      }
      if (this.active === 1) this.removeUnfinishedMeasurements();
      if (this.selectedMeasurementArea)
        this.unselectFromSidebar(this.selectedMeasurementArea);
      this.showMeasurementFromSidebar(distanceId);
    },
    async shareSolarModel() {
      const payload = {
        language: this.$route.query.lang,
        project_id: this.project.id,
        is_bobbie: this.isBobbieContext,
      };
      const { data } = await API.projects.shareFastFusionSolarModel(payload);

      let copiedLink = window.location.href;
      if (!this.anonymousUser) {
        copiedLink = data.link;
      }

      // safari only allows using the clipboard in response to direct user action.
      // but not in response to api calls.
      // this is a workaround using setTimeout.
      setTimeout(async () => {
        await navigator.clipboard.writeText(copiedLink).catch((err) => {
          ElMessage.error({
            message: "Error occurred while copying solar model sharing link",
            showClose: true,
          });
        });
      }, 0);

      if (!TEST_COMPANIES.includes(this.company.id)) {
        trackEvent("share_ffsolar_model", {
          event_label: "FFSolar model shared",
          method: "copy",
          project_id: this.project.id,
          customer_type: this.currentUser.isEmployee ? "employee" : "customer",
        });
      }
      this.dialogVisible = true;
    },
    init() {
      this.container = document.createElement("div");
      this.selectionBox = document.createElement("div");
      this.selectionBox.style.border = "3px solid var(--color-map-green)";
      this.selectionBox.style.position = "absolute";
      this.selectionBox.style.pointerEvents = "none";

      this.$nextTick(() => {
        this.$refs.airteamViewerDiv.appendChild(this.container);
        this.$refs.airteamViewerDiv.appendChild(this.selectionBox);
        this.canvasElementRef = document.querySelector("canvas");
        document.querySelector("canvas").style.height = "100%";
        document.querySelector("canvas").dataset.sl = "canvas-hq";
      });

      /* Scene */
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x000000);

      this.ambient = new THREE.AmbientLight(WHITE, 3.0);
      this.scene.add(this.ambient);

      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
        stencil: true,
      });
      this.renderer.autoClear = false;
      this.currentPixelRatio = window.devicePixelRatio;
      this.renderer.setPixelRatio(Math.min(this.currentPixelRatio, 2));
      this.renderer.setSize(window.innerWidth * this.getViewerWidth, window.innerHeight);
      this.renderer.setClearColor(new THREE.Color("hsl(0, 0%, 10%)"));
      this.renderer.domElement.style.cursor = "grab";

      this.renderer.toneMapping = THREE.NoToneMapping;
      this.renderer.outputEncoding = THREE.sRGBEncoding;

      this.container.appendChild(this.renderer.domElement);

      /* Label Renderer */
      this.labelRenderer = new CSS2DRenderer();
      this.labelRenderer.setSize(
        window.innerWidth * this.getViewerWidth,
        window.innerHeight
      );
      this.labelRenderer.domElement.style.position = "absolute";
      this.labelRenderer.domElement.style.top = "0px";
      this.labelRenderer.domElement.style.pointerEvents = "none";
      this.container.appendChild(this.labelRenderer.domElement);

      // arrow
      this.arrowScene = new THREE.Scene();
      const light = new THREE.AmbientLight(WHITE, 3.0);
      this.arrowScene.add(light);

      this.arrowRenderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
      });
      this.arrowRenderer.setSize(50, 50);
      this.$nextTick(() => {
        const arrowContainer = document.getElementById("arrow-container");
        arrowContainer.style.borderRadius = "50%";
        arrowContainer.appendChild(this.arrowRenderer.domElement);
        document.querySelector("canvas").style.height = "100%";
      });

      this.markers = [];
      this.additionalMarkers = [];

      this.raycaster = new THREE.Raycaster();
      this.mouse = new THREE.Vector2();
      this.spriteMaterial = new THREE.SpriteMaterial({
        map: new THREE.TextureLoader().load(newMarker),
      });
      this.getDefaultPanelTypes().then((allPanelTypes) => {
        allPanelTypes.forEach((type) => {
          if (type.orientation === VERTICAL) {
            this.verticalPanelTypes.push(type);
          } else {
            this.horizontalPanelTypes.push(type);
          }
        });
        this.loadPanelTextures();
      });

      this.frustum = new THREE.Frustum();

      this.dotGeometry = new THREE.SphereGeometry(0.1, 32, 32);
      this.middotGeometry = new THREE.SphereGeometry(0.075, 32, 32);

      this.panelVerticesCache = new Map();

      /* Events */
      window.addEventListener("resize", this.onWindowResize, false);
      window.addEventListener("blur", this.resetCommandKeyStates, false);

      // Initialize stats.js
      if (this.currentUser.isEmployee) {
        this.stats = new Stats();
        this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
        document.body.appendChild(this.stats.dom);

        // Style the stats panel to position it on the right side
        this.stats.dom.style.position = "fixed";
        this.stats.dom.style.right = "15%";
        this.stats.dom.style.left = "auto";
        this.stats.dom.style.top = "1rem";
      }
    },
    initializeCamera() {
      /* Camera */
      this.perspectiveCamera = new THREE.PerspectiveCamera(
        50,
        (window.innerWidth * this.getViewerWidth) / window.innerHeight,
        0.01,
        1000
      );
      this.camera = this.perspectiveCamera;

      const zoomOutFactor = this.calculateModelHeight() * 2;
      const {
        xFactor,
        yFactor,
        zFactor,
        xTarget,
        yTarget,
        zTarget,
      } = this.calculateCameraPosition(zoomOutFactor);
      this.camera.position.set(xFactor, yFactor, zFactor);
      this.camera.lookAt(this.modelObject.position);

      this.cameraControls = new CameraControls(this.camera, this.renderer.domElement);

      this.cameraControls.minDistance = 2;
      this.cameraControls.maxDistance = zoomOutFactor * (3 / 2);

      if (xTarget) this.cameraControls.setTarget(xTarget, yTarget, zTarget, true);

      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.middle = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE;
    },
    initializeNorthArrowCamera() {
      this.arrowCamera = new THREE.PerspectiveCamera(
        40,
        (window.innerWidth * this.getViewerWidth) / window.innerHeight,
        0.01,
        1000
      );
      this.arrowCamera.position.set(0, 5, 0);
      this.arrowCamera.lookAt(this.arrowHelper.position);
    },
    toggleCameraMode() {
      const currentPosition = this.camera.position.clone();
      const currentTarget = new THREE.Vector3();
      this.cameraControls.getTarget(currentTarget);

      const aspect = (window.innerWidth * this.getViewerWidth) / window.innerHeight;

      if (this.isOrthographicCamera) {
        this.camera = this.perspectiveCamera;
        this.isOrthographicCamera = false;
      } else {
        const frustumSize = currentPosition.distanceTo(currentTarget);
        if (!this.orthographicCamera) {
          this.orthographicCamera = new THREE.OrthographicCamera(
            (frustumSize * aspect) / -2,
            (frustumSize * aspect) / 2,
            frustumSize / 2,
            frustumSize / -2,
            0.01,
            1000
          );
        } else {
          this.orthographicCamera.left = (frustumSize * aspect) / -2;
          this.orthographicCamera.right = (frustumSize * aspect) / 2;
          this.orthographicCamera.top = frustumSize / 2;
          this.orthographicCamera.bottom = frustumSize / -2;
          this.orthographicCamera.updateProjectionMatrix();
        }
        this.camera = this.orthographicCamera;
        this.isOrthographicCamera = true;
      }

      this.camera.position.copy(currentPosition);
      this.camera.lookAt(currentTarget);

      this.cameraControls.dispose();
      this.cameraControls = new CameraControls(this.camera, this.renderer.domElement);

      const zoomOutFactor = this.calculateModelHeight() * 2;
      this.cameraControls.minDistance = 2;
      this.cameraControls.maxDistance = zoomOutFactor * (3 / 2);
      this.cameraControls.setTarget(
        currentTarget.x,
        currentTarget.y,
        currentTarget.z,
        false
      );

      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.middle = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE;

      if (this.transformControls) {
        this.transformControls.camera = this.camera;
      }
    },
    renderScene() {
      this.initializeCamera();

      document.addEventListener("keydown", this.keyDownInit);

      document.addEventListener("keyup", this.keyUpInit);

      document.addEventListener("keydown", this.escClicked, false);
      document.addEventListener("keydown", this.hotKeyClicked, false);

      window.addEventListener("keydown", this.keyDownInitWindow);

      window.addEventListener("dblclick", this.addZoomCenter, false);

      window.addEventListener("wheel", this.hideAuxiliaryValuesOnZoom, false);

      this.animate();
    },
    onProgress(event) {
      const progress = (event.loaded / event.total).toFixed(0);
      this.loadingProgress = Number(progress);
    },
    async showModel(modelName, baseUrl) {
      const uuid = crypto.randomUUID();
      const loader = new GLTFLoader();
      loader.setPath(baseUrl);
      loader.load(
        modelName + `?cache=${uuid}`,
        async (gltf) => {
          this.modelObject = gltf.scene;
          this.setThrottleInterval();
          this.exportedManually = this.modelObject.children[0].userData?.exported_manually;
          this.fixModelRotation();
          if (this.exportedManually && this.project.orientation.x === -90) {
            this.project.orientation.x = 90;
          }
          this.orientationUpdated();
          this.scalingUpdated(this.project.scale, true);
          this.renderScene();
          this.scene.add(gltf.scene);
          if (!this.sample || this.project.isSampleProject) {
            await this.loadPanelTextures();
            await this.displayExistingModelObjects();
            this.objectIsloaded = true;
            if (!this.anonymousUser) {
              await Promise.all([
                this.getUserDefaultSolarValues(),
                this.getUserLikedPanel(),
              ]);
            }
          }
          this.initializeLineWorker();
          this.initializeTransformControls();
          this.toggleActive(0);
          this.loading = false;
          this.onWindowResize();

          if (this.project.isSampleProject) {
            return ElMessage.warning({
              message: this.$t("sampleProjects.warning"),
              duration: 0,
            });
          }
        },
        this.onProgress
      );
    },
    setThrottleInterval() {
      const geometry = this.modelObject.children[0].geometry;
      const triangleCount = geometry.index.count / 3;
      const intervalStep = Math.floor(triangleCount / 100000) * 10;
      this.throttleInterval = Math.max(intervalStep, this.throttleInterval);
    },
    calculateModelHeight() {
      let modelHeight = 50;

      this.modelObject.traverse((child) => {
        if (child.isMesh) {
          const geometry = child.geometry;

          geometry.computeBoundingBox();
          const boundingBox = geometry.boundingBox;
          const minY = boundingBox.min.y;
          const maxY = boundingBox.max.y;

          const height = maxY - minY;

          if (height) modelHeight = height;
        }
      });

      // TO BE REPLACED DURING GOOGLE TILES PROJECT
      if (this.projectId == 54171) modelHeight = 50;

      return modelHeight * this.project.scale;
    },
    onWindowResize() {
      this.camera.aspect = (window.innerWidth * this.getViewerWidth) / window.innerHeight;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(window.innerWidth * this.getViewerWidth, window.innerHeight);
      this.labelRenderer.setSize(
        window.innerWidth * this.getViewerWidth,
        window.innerHeight
      );

      this.updateLineWorkerCamera();
    },
    animate() {
      if (this.stats) {
        this.stats.begin(); // Start measuring
      }

      requestAnimationFrame(this.animate);
      const delta = this.clock.getDelta();
      this.cameraControls.update(delta);
      this.updateNorthArrow();
      this.updateAllReactivePoints();
      this.updateAllInstancedMeshPoints();
      this.render();
      H.snapshot(this.canvasElementRef);

      if (this.stats) {
        this.stats.end(); // End measuring
      }
    },
    render() {
      this.labelRenderer.render(this.scene, this.camera);
      this.renderer.render(this.scene, this.camera);
      this.arrowRenderer.render(this.arrowScene, this.arrowCamera);
    },
    async showBridge(modelName, baseUrl) {
      if (!this.sample || this.project.isSampleProject)
        baseUrl = baseUrl[baseUrl.length - 1] === "/" ? baseUrl : baseUrl + "/";
      else {
        baseUrl = window.runtimeConfig?.STATIC_MODELS_URL;
        modelName = `${this.sampleModelName}.glb`;
      }

      await this.showModel(modelName, baseUrl);
      this.loadCamera();
      await this.loadNorthArrow();
    },
    toggleActive(value, fromPanOrbit = false) {
      this.updateMeasurementLabelCursors(value);

      if (this.active === 5 && value !== 5 && this.camerasDisplayed) {
        this.changeCamerasVisibility(false);
      }
      if (value != this.active) {
        if (this.isMeasurementSelected) {
          this.unselectDistances();
        }
        if (this.selectedMeasurementArea) {
          this.unselectArea();
        }
        if (this.selectedArea && value !== 3 && value !== 6) {
          this.unselectSolarAreaOnSidebar();
        }
        if (value === 5) {
          this.removeUnfinishedMeasurements();
          this.unselectDistances();
        }
      }

      if (value !== 1 && value !== 4) this.measurementModeActive = false;
      if (value === 1 || value === 4) {
        this.measurementModeActive = true;
        this.measurementMenuExpanded = true;
      }
      if (
        (value === 1 || value === 2 || value === 4) &&
        !this.anonymousUser &&
        (!this.sample || this.project.isSampleProject)
      ) {
        this.$refs.mainSidebaRef.handleModeChange(value);
      }

      this.toggleView(value, fromPanOrbit);
    },

    escClicked(event) {
      const key = event.key;

      if (key === "Escape") {
        // Check for measurement deselection when active is 0 or 1
        if ((this.active === 0 || this.active === 1) && this.isMeasurementSelected) {
          this.unselectDistances();
        }
        // Check for measurement area deselection when active is 0 or 4
        else if (
          (this.active === 0 || this.active === 4) &&
          this.selectedMeasurementArea
        ) {
          this.unselectArea();
        }
        // Handle other cases
        else if (this.active === 1) {
          this.removeUnfinishedMeasurements();
        } else if (this.active === 2) {
          this.removeUnfinshedArea();
          this.unselectSolarArea();
        } else if (this.active === 3) {
          this.disableIndividualPlacementMode();
          if (this.selectedArea && this.selectedArea.restrictedAreas.length > 0) {
            this.showRestrictedAreaDetails(this.selectedArea);
          }
        } else if (this.active === 4) {
          this.removeUnfinishedAreaMeasurements();
        } else if (this.active === 6) {
          this.disableRestrictedAreaMode();
        }
      }
    },
    hotKeyClicked(event) {
      const key = event.key;

      if (this.fullScreenImageActive) {
        return;
      }
      if (key === "s") {
        if (this.active === 2 && this.selectedArea) {
          this.toggleActive(6);
        } else if (this.active === 6) {
          this.disableRestrictedAreaMode();
        }
      } else if (key === "e") {
        if (this.active === 2 && this.selectedArea) {
          this.enableIndividualPlacementMode("VERTICAL");
        } else if (this.active === 3) {
          this.disableIndividualPlacementMode();
        }
      } else if (key === "w") {
        if (this.active === 3) {
          this.toggleIndividualPanelOrientation();
        }
      } else if (key === "d") {
        this.toggleActive(1);
      } else if (key === "f") {
        this.toggleActive(4);
      }
    },
    toggleHotkeys(enabled) {
      if (!enabled) {
        document.removeEventListener("keydown", this.hotKeyClicked);
      } else {
        document.addEventListener("keydown", this.hotKeyClicked);
      }
    },
    toggleView(view, fromPanOrbit = false) {
      this.cleanNavigationSetup();
      this.resetUndoStack();
      this.resetRedoStack();

      this.modes.forEach((mode) => {
        if (mode.enabled && view !== 3 && view !== 6) {
          mode.disableFunction.call(this, view);
          if (mode.value === 3 || mode.value === 6) {
            this.modes.find((mode) => mode.value === 2).disableFunction();
          }
        }
      });

      if (this.active === 3 || this.active === 6) {
        this.modes.find((mode) => mode.value === this.active).disableFunction();
      }

      this.active = view;

      const currentMode = this.modes.find((mode) => mode.value === view);

      if (currentMode) {
        currentMode.enableFunction.call(this, fromPanOrbit);
      }
      if (view === null || view === 0) {
        this.modes.find((mode) => mode.value === 2).disableFunction();
      }
    },

    closeDragElement() {
      document.onmouseup = null;
      document.onmousemove = null;
    },
    toggleSidebar(event) {
      this.sidebarOpen = !this.sidebarOpen;
      this.onWindowResize();
      event.stopPropagation();
    },

    takeScreenshot() {
      if (this.active !== 0) return;
      // take screenshot
      let img = new Image();
      this.renderer.render(this.scene, this.camera);
      img.src = this.renderer.domElement.toDataURL();
      let msg = this.$t("screenshot.success");
      if (this.screenshot) msg = this.$t("screenshot.overwrite");
      this.screenshot = img;
      ElMessage.success({
        message: msg,
        showClose: true,
        duration: "2500",
      });
    },
    printPage() {
      if (!TEST_COMPANIES.includes(this.company.id)) {
        trackEvent("generate_ffsolar_report", {
          page_title: "AirteamViewer",
          page_location: window.location.href,
          page_path: window.location.pathname,
          project_id: this.project.id,
          isCustomer: !this.currentUser.isEmployee,
          customer_type: this.currentUser.isEmployee ? "employee" : "customer",
        });
      }
      let sizeToPanelNumberUl =
        this.$i18n.locale === "de"
          ? "Anzahl der Solarmodule pro Größe: <ul>"
          : "Amount of solar panels per size: <ul>";
      let sizeToPanelSurfaceUl =
        this.$i18n.locale === "de" ? "Fläche pro Größe: <ul>" : "Surface per size: <ul>";
      const [sizeToPanelNumber, sizeToPanelSurface] = this.panelsDataPerSize;

      let totalPitchAngle = 0;
      let totalSurfaceArea = 0;
      for (let area of this.areas.filter((area) => area.closed && area.show !== false)) {
        const surfaceArea = this.calculateSurfaceArea(area);
        totalPitchAngle += surfaceArea * area.angle;
        totalSurfaceArea += Number(surfaceArea);
      }
      const averagePitchAngle = (totalPitchAngle / totalSurfaceArea).toFixed(2);

      Object.entries(sizeToPanelNumber).forEach(([size, panelNumber]) => {
        sizeToPanelNumberUl += `<li>${size}: ${panelNumber}</li>`;
      });
      sizeToPanelNumberUl += "</ul>";

      Object.entries(sizeToPanelSurface).forEach(([size, panelSurface]) => {
        sizeToPanelSurfaceUl += `<li>${size}: ${panelSurface.toFixed(2)}m²</li>`;
      });
      sizeToPanelSurfaceUl += "</ul>";

      const project = this.$i18n.locale === "de" ? "Projekt" : "Project";
      const numberOfSolarPanels =
        this.$i18n.locale === "de" ? "Anzahl der Solarmodule" : "Number of solar panels";
      const totalPanelSurface =
        this.$i18n.locale === "de"
          ? "Gesamtfläche der Solarmodule"
          : "Total solar panel surface";
      const averagePitchAngleTitle =
        this.$i18n.locale === "de"
          ? "Durchschnittlicher Neigungswinkel des Bereichs"
          : "Average area pitch angle";

      const pitchAngleTitle = (id) =>
        this.$i18n.locale === "de"
          ? `Bereich ${id} Steigungswinkel`
          : `area ${id} pitch angle`;

      const pitchAnglePerArea =
        this.$i18n.locale === "de" ? "Neigungswinkel pro Fläche" : "Pitch angle per area";

      const h4 = this.sample ? "Sample Project" : `${project} Id: ${this.project.id}`;
      let printData = `<h4>${h4}</h4><p>${numberOfSolarPanels}: ${this.totalNumberOfPanels}</p><p>${totalPanelSurface}: ${this.totalPanelsArea}m²</p><p>${averagePitchAngleTitle}: ${averagePitchAngle}°</p>`;
      printData += sizeToPanelNumberUl;
      printData += sizeToPanelSurfaceUl;

      const closedAreas = this.areas.filter((area) => area.closed && area.show !== false);
      if (closedAreas.length > 0) printData += `<p>${pitchAnglePerArea}:</p><ul>`;
      for (let i = 0; i < closedAreas.length; i++) {
        printData += `<li>${pitchAngleTitle(i + 1)}: ${closedAreas[i].angle}°</li>`;
      }
      if (closedAreas.length > 0) printData += "</ul>`";
      if (this.screenshot) printData += `<img src=${this.screenshot.src} width="500"/>`;
      this.$htmlToPaper(printData);
    },
    undo() {
      if (this.undoStack.length === 0 || this.undoLoading) return;
      this.undoLoading = true;
      const lastAction = this.undoStack.pop();
      switch (lastAction.action) {
        case "ADD_MEASUREMENT_POINT":
          this.undoAddMeasurementPoint(lastAction.measurement);
          break;
        case "ADD_POINT":
          this.undoAddPoint(
            lastAction.area,
            lastAction.point,
            lastAction.line,
            lastAction.midPoint,
            lastAction.oldTempLine,
            lastAction.oldFirstPoint,
            lastAction.newTempLine,
            lastAction.oldTempLabel,
            lastAction.newTempLabel,
            lastAction.fixedLine
          );
          break;
        case "MOVE_POINT":
          this.undoMovePoint(
            lastAction.areaType,
            lastAction.area,
            lastAction.index,
            lastAction.position
          );
          break;
        case "MOVE_MEASUREMENT_POINT":
          this.undoMoveMeasurementPoint(lastAction.measurement, lastAction.point);
          break;
        case "MOVE_MID_POINT":
          this.undoMoveMidPoint(
            lastAction.area,
            lastAction.index,
            lastAction.isMeasurement
          );
          break;
        case "DELETE_AREA":
          this.undoDeleteArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT_AREA":
          this.undoDeleteMeasurementArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT":
          this.undoDeleteMeasurement(lastAction.measurement);
          break;
        case "ADD_PANEL":
          this.undoAddPanel(lastAction.panel, lastAction.area);
          break;
        case "BULK_ADD_PANELS":
          this.undoBulkAddPanels(lastAction.panels, lastAction.area);
          break;
        case "MOVE_PANELS":
          this.undoMovePanels(lastAction.panels, lastAction.area);
          break;
        case "DELETE_PANELS":
          this.undoDeletePanels(lastAction.panels, lastAction.area);
          break;
        case "MERGE_POINT":
          this.undoMergePoint(
            lastAction.area,
            lastAction.points,
            lastAction.isMeasurement
          );
          break;
        default:
          null;
      }
      this.undoLoading = false;
    },
    redo() {
      if (this.redoStack.length === 0 || this.redoLoading) return;
      this.redoLoading = true;
      const lastAction = this.redoStack.pop();
      switch (lastAction.action) {
        case "ADD_MEASUREMENT_POINT":
          this.redoAddMeasurementPoint(lastAction.measurement);
          break;
        case "ADD_POINT":
          this.redoAddPoint(
            lastAction.area,
            lastAction.point,
            lastAction.line,
            lastAction.midPoint,
            lastAction.oldTempLine,
            lastAction.oldFirstPoint,
            lastAction.newTempLine,
            lastAction.oldTempLabel,
            lastAction.newTempLabel,
            lastAction.fixedLine
          );
          break;
        case "MOVE_POINT":
          this.redoMovePoint(
            lastAction.areaType,
            lastAction.area,
            lastAction.index,
            lastAction.position
          );
          break;
        case "MOVE_MEASUREMENT_POINT":
          this.redoMoveMeasurementPoint(lastAction.measurement, lastAction.point);
          break;
        case "MOVE_MID_POINT":
          this.redoMoveMidPoint(
            lastAction.area,
            lastAction.index,
            lastAction.points,
            lastAction.isMeasurement
          );
          break;
        case "DELETE_AREA":
          this.redoDeleteArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT_AREA":
          this.redoDeleteMeasurementArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT":
          this.redoDeleteMeasurement(lastAction.measurement);
          break;
        case "ADD_PANEL":
          this.redoAddPanel(lastAction.panel, lastAction.area);
          break;
        case "BULK_ADD_PANELS":
          this.redoBulkAddPanels(lastAction.panels, lastAction.area);
          break;
        case "MOVE_PANELS":
          this.redoMovePanels(lastAction.panels, lastAction.area);
          break;
        case "DELETE_PANELS":
          this.redoDeletePanels(lastAction.panels, lastAction.area);
          break;
        case "MERGE_POINT":
          this.redoMergePoint(
            lastAction.area,
            lastAction.points,
            lastAction.isMeasurement
          );
          break;
        default:
          null;
      }
      this.redoLoading = false;
    },
    resetRedoStack() {
      this.redoStack = [];
    },
    resetUndoStack() {
      this.undoStack = [];
    },
    fixModelRotation() {
      // this.modelObject.children[0].rotation.x = Math.PI * (-90 / 180);
      // this.project.orientation.x = -90;
    },
    calculateCameraPosition(zoomOutFactor) {
      if (!this.sample || this.thermal || this.project.isSampleProject)
        return {
          xFactor: 0,
          yFactor: zoomOutFactor,
          zFactor: 0,
        };
      return this.getSampleModelCameraPosition();
    },
    getSampleModelCameraPosition() {
      switch (this.sampleModelName) {
        case "sample-1":
          return {
            xFactor: -49.7,
            yFactor: 30.9,
            zFactor: -6.8,
          };
        case "sample-2":
          return {
            xFactor: 29.4,
            yFactor: 36.4,
            zFactor: 35.7,
          };
        case "sample-3":
          return {
            xFactor: -12.4,
            yFactor: 37.3,
            zFactor: 41.9,
          };
        case "sample-4":
          return {
            xFactor: 35.5,
            yFactor: 91.2,
            zFactor: 80.6,
            xTarget: -3.5,
            yTarget: 18,
            zTarget: -17,
          };
      }
    },
    orientationUpdated() {
      this.modelObject.children[0].rotation.x = THREE.MathUtils.degToRad(
        this.project.orientation.x
      );
      this.modelObject.children[0].rotation.y = THREE.MathUtils.degToRad(
        this.project.orientation.y
      );
      this.modelObject.children[0].rotation.z = THREE.MathUtils.degToRad(
        this.project.orientation.z
      );

      this.modelObject.children[0].updateMatrixWorld(true);
    },
    async scalingUpdated(scalingValue, isInitialization) {
      this.removeMeasurements();
      if (scalingValue) {
        this.modelObject?.scale.set(scalingValue, scalingValue, scalingValue);
        if (!isInitialization) {
          await API.projects.patchProject(this.project.id, {
            solarModelScale: scalingValue,
          });
          this.project.scale = scalingValue;
        }
      }
    },
    async saveOrientation() {
      let orientationData = {
        x: this.project.orientation.x || 0,
        y: this.project.orientation.y || 0,
        z: this.project.orientation.z || 0,
      };
      const orientation = JSON.stringify(orientationData);
      await API.projects.patchProject(this.project.id, {
        solarModelOrientation: orientation,
      });
      this.project.orientation = orientationData;

      this.savedOrientation = {
        x: orientationData.x,
        y: orientationData.y,
        z: orientationData.z,
      };
    },
    resetOrientation() {
      this.project.orientation.x = this.savedOrientation.x;
      this.project.orientation.y = this.savedOrientation.y;
      this.project.orientation.z = this.savedOrientation.z;
      this.orientationUpdated();
    },
    initializeTransformControls() {
      if (!this.modelObject || this.transformControls) return;

      this.transformControls = new TransformControls(
        this.camera,
        this.renderer.domElement
      );

      this.transformControls.setMode("rotate");

      this.transformControls.setSpace("world");

      this.transformControls.setSize(1.5);

      // Rotation speed controls sensitivity - lower = less sensitive
      this.transformControls.setRotationSpeed(5);

      this.transformControls.setRotationSnap(THREE.MathUtils.degToRad(0.001));

      this.transformControls.attach(toRaw(this.modelObject.children[0]));

      // Update UI when rotation changes
      this.transformControls.addEventListener("change", () => {
        if (this.transformControls.dragging) {
          const rotation = this.modelObject.children[0].rotation;
          this.project.orientation.x = THREE.MathUtils.radToDeg(rotation.x);
          this.project.orientation.y = THREE.MathUtils.radToDeg(rotation.y);
          this.project.orientation.z = THREE.MathUtils.radToDeg(rotation.z);
        }
      });

      this.transformControls.addEventListener("dragging-changed", (event) => {
        this.cameraControls.enabled = !event.value;
      });

      this.scene.add(toRaw(this.transformControls));

      // Hide the outer ring (E/XYZE) and center free rotation sphere (XYZ)
      this.transformControls.children.forEach((child) => {
        if (child.type === "TransformControlsGizmo") {
          child.traverse((obj) => {
            if (obj.name === "E" || obj.name === "XYZE" || obj.name === "XYZ") {
              obj.visible = false;
              obj.scale.set(0, 0, 0);
              obj.layers.disable(0);
            }
          });
        }
        if (child.type === "TransformControlsPlane") {
          child.traverse((obj) => {
            if (obj.name === "E" || obj.name === "XYZE" || obj.name === "XYZ") {
              obj.visible = false;
              obj.scale.set(0, 0, 0);
              obj.layers.disable(0);
            }
          });
        }
      });

      const picker = this.transformControls.children.find(
        (child) => child.name === "picker"
      );
      if (picker) {
        picker.traverse((obj) => {
          if (obj.name === "E" || obj.name === "XYZE" || obj.name === "XYZ") {
            obj.visible = false;
            obj.scale.set(0, 0, 0);
            obj.layers.disable(0);
          }
        });
      }

      this.transformControls.enabled = false;
      this.transformControls.visible = false;
    },
    enableTransformControls() {
      if (!this.transformControls) {
        this.initializeTransformControls();
      }

      if (this.transformControls) {
        this.transformControls.enabled = true;
        this.transformControls.visible = true;
      }

      if (!this.infiniteGridHelper) {
        const size = 1000;
        const divisions = 100;
        const centerColor = 0x444444;
        const gridColor = 0x888888;

        this.infiniteGridHelper = new THREE.GridHelper(
          size,
          divisions,
          centerColor,
          gridColor
        );
        this.infiniteGridHelper.material.opacity = 0.5;
        this.infiniteGridHelper.material.transparent = true;
        this.infiniteGridHelper.material.depthWrite = false;
        this.infiniteGridHelper.renderOrder = -1;

        const modelCenter = this.modelObject.position;
        this.infiniteGridHelper.position.copy(modelCenter);

        this.scene.add(toRaw(this.infiniteGridHelper));
      } else {
        this.infiniteGridHelper.visible = true;
      }
    },
    disableTransformControls() {
      if (!this.transformControls) return;

      this.transformControls.enabled = false;
      this.transformControls.visible = false;

      // Hide infinite grid
      if (this.infiniteGridHelper) {
        this.infiniteGridHelper.visible = false;
      }
    },
    trackPageView() {
      if (
        this.galleryPointsSelected > 0 &&
        !TEST_COMPANIES.includes(this.company.id)
      ) {
        trackEvent("gallery_mode_session", {
          project_id: this.project.id,
          company_id: this.company.id,
          anonymous_user: this.anonymousUser,
          customer_type: this.currentUser.isEmployee ? "employee" : "customer",
          gallery_points_selected: this.galleryPointsSelected,
        });
      }
    },
    detectModelIntersection(event) {
      if (this.active === 1) {
        this.updatePreliminaryPointPosition(event);
      } else if (this.active === 2) {
        this.updatePreliminaryPointPositionForArea(event);
      } else if (this.active === 4) {
        this.updatePreliminaryPointPositionForMeasurementArea(event);
      } else if (this.active === 6) {
        this.updatePreliminaryPointPositionForRestrictedArea(event);
      }
    },
    displayExistingObject(element) {
      if (element.type === "MEASUREMENT") {
        this.existingMeasurementsIds.push(element.id);
        element?.position.forEach((point) => {
          this.displayMarker(element, point);
        });
        this.measurements[this.measurements.length - 1].name = element.name;
      } else if (["AREA", "SOLAR_GROUP"].includes(element.type)) {
        this.existingAreasIds.push(element.id);
        this.displayAreaPoint(element, false);
        this.areas[this.areas.length - 1].id = element.id;
        this.areas[this.areas.length - 1].show = element.show;
        this.areas[this.areas.length - 1].type = element.type;
        this.areas[this.areas.length - 1].name = element.name;

        if (element.type === "SOLAR_GROUP") {
          this.useStoredSolarValues(this.areas[this.areas.length - 1], element);
        }

        this.drawPlane(this.areas[this.areas.length - 1]?.points, false);

        this.areas[this.areas.length - 1].restrictedAreas = element.restricted_areas;

        this.selectedArea = this.areas[this.areas.length - 1];
        this.processRestrictedAreas(this.areas[this.areas.length - 1]);

        this.displayIndividualPanels(this.areas[this.areas.length - 1]);
        if (element.show === false) {
          this.hideSingleArea(this.areas[this.areas.length - 1], true);
        }
      } else if (element.type === "PANEL") {
        this.displayPanel(element);
      } else if (element.type === "AREA_MEASUREMENT") {
        this.existingMeasurementsIds.push(element.id);
        this.displayAreaPoint(element, true);
        this.measurementAreas[this.measurementAreas.length - 1].id = element.id;
        this.measurementAreas[this.measurementAreas.length - 1].show = element.show;
        this.measurementAreas[this.measurementAreas.length - 1].name = element.name;

        this.drawMeasurementAreaPlane(
          this.measurementAreas[this.measurementAreas.length - 1]?.points,
          false
        );
        if (this.measurementAreas[this.measurementAreas.length - 1].show === false) {
          this.hideSingleMeasurementArea(
            this.measurementAreas[this.measurementAreas.length - 1]
          );
        }
      }
    },
    async displayExistingModelObjects() {
      const api = await API.airteam3DViewer.getFfSolarObjectsByProjectId(this.project.id);
      const elements = api.data;
      elements
        .sort((a, b) => {
          const typeOrder = {
            AREA: 1,
            AREA_MEASUREMENT: 2,
            MEASUREMENT: 3,
            PANEL: 4,
          };

          return typeOrder[a.type] - typeOrder[b.type];
        })
        .forEach((element) => {
          this.displayExistingObject(element);
        });
      this.hideAreas(false);
      this.setPanelsOpaque();
      if (this.anonymousUser) {
        this.checkSectionsVisibility();
      }
    },
    showSmallMeasurementMenu() {
      if (this.active === 1 || this.active === 4) {
        this.measurementMenuExpanded = false;
      }
    },
    async removeCustomPanel(event, panelToRemove) {
      const customPanels = this.customPanels.map((panel) => {
        const updatedPanel = { ...panel };
        if (panel.name === panelToRemove.name) {
          updatedPanel.show = false;
        }
        return updatedPanel;
      });
      this.setNewCustomPanels(customPanels);
      try {
        await API.airteam3DViewer.updateCustomPanel({
          id: panelToRemove.id,
          show: false,
        });
      } catch (error) {
        console.error("Failed to update custom panel:", error);
      }
      event.stopPropagation();
    },
    async loadNorthArrow() {
      const loader = new GLTFLoader();
      loader.load("/assets/model/northarrow_v2.glb", (gltf) => {
        this.arrowHelper = gltf.scene;
        this.arrowScene.add(this.arrowHelper);
        this.initializeNorthArrowCamera();
      });
    },
    updateNorthArrow() {
      this.arrowCamera.quaternion.copy(this.camera.quaternion);

      const forward = new THREE.Vector3(0, 0, -1);
      forward.applyQuaternion(this.camera.quaternion);

      const distance = 5;

      const newPosition = new THREE.Vector3()
        .copy(this.arrowHelper.position)
        .add(forward.multiplyScalar(-distance));

      this.arrowCamera.position.copy(newPosition);

      this.arrowCamera.lookAt(this.arrowHelper.position);
    },
    handleOrbitClick() {
      if (this.navigationMenu === 0) {
        // If already in orbit mode, switch back to select mode
        this.toggleActive(this.previousMode, true);
        this.previousMode = null;
        this.navigationMenu = null;
      } else {
        // Otherwise switch to orbit mode
        this.toggleNavigation(0);
      }
    },
    handlePanClick() {
      if (this.navigationMenu === 1) {
        // If already in pan mode, switch back to select mode
        this.toggleActive(this.previousMode, true);
        this.previousMode = null;
        this.navigationMenu = null;
      } else {
        // Otherwise switch to pan mode
        this.toggleNavigation(1);
      }
    },
    handleGalleryClick() {
      if (this.active === 5) {
        // if already in gallery mode, do nothing
        return;
      } else {
        // Otherwise switch to gallery mode
        this.toggleActive(5);
      }
    },
    async checkSectionsVisibility() {
      const config = await API.airteam3DViewer.getProjectConfig(this.project.id);
      if (!config.data.distances_visible) {
        this.hideMeasurements();
      }
      if (!config.data.measurements_visible) {
        this.hideAllMeasurementAreas();
      }

      if (!config.data.solar_groups_visible) {
        this.hideAreas();
      }
    },
    toggleFullScreenImageActive(value) {
      this.fullScreenImageActive = value;
    },
    handlePageShow(event) {
      this.checkAuthenticationStatus();
    },
    checkAuthenticationStatus() {
      if (!this.sample && !this.anonymousUser) {
        const key = `${isProduction ? "" : "staging"}loggedoutstatus`;
        const loggedOutStatus = getCookie(key);
        if (loggedOutStatus) {
          authZitadelMethods.noCurrentProject();
          return false;
        }
      }
      return true;
    },
  },
};
</script>

<script setup>
import { useDownloadAllDialog } from "@/composables/useDownloadAllDialog";

const { showDownloadAllDialog } = useDownloadAllDialog();
</script>

<style scoped lang="scss">
@use "@/scss/_typography" as typography;
@use "@/scss/_colors" as colors;

.project-panel {
  &__summary {
    @include typography.getFont($font-name: subtitle1);
    color: white;
  }
  &__field {
    @include typography.getFont($font-name: subtitle3);
    font-size: 12px;
    display: flex;
    justify-content: space-between;

    &--label {
      color: white;
      font-weight: 400;
    }

    &--value {
      color: white;
      font-weight: 200;
    }
  }
  &__settings {
    justify-content: space-between;
  }
  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__solar-card {
    border-radius: 8px;
    border: 0;
    background: colors.$GREB50;

    &-header {
      border-radius: 8px;
    }

    &-content {
      background: colors.$GREB50;
    }
  }
}

.breadcrumbs {
  &__container {
    background-color: var(--color-dark-bg-secondary);
    color: white;
  }

  &__last {
    font-weight: bold;
    color: white !important;
  }
}

.new-sidebar-layout {
  background: var(--color-dark-bg-primary);
  color: white;
  height: 100vh;

  p {
    color: white;
  }
  span {
    color: white;
  }
}
.sidebar-bottom {
  max-height: 44rem;
  overflow-y: scroll;
  scrollbar-width: none;
}

hr.project-panel__line {
  margin-left: -24px;
  margin-right: -24px;
}

.close-sidebar {
  margin-right: -35px;

  div {
    background-color: var(--color-white);
    z-index: 9999;
    position: relative;
    border: solid 1px var(--color-border-light);
    border-radius: 10px;
  }
}

.open-sidebar {
  position: fixed;
  top: 70px;
  z-index: 9999;

  div {
    background-color: var(--color-white);
    z-index: 9999;
    position: relative;
    border: solid 1px var(--color-border-light);
    border-radius: 10px;
  }
}

.panels__toolbar {
  background: colors.$GREB50;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  padding: 10px 0px;
  z-index: 15 !important;

  &--small {
    padding: 5px;
  }

  &--area {
    padding-right: 0;
  }

  &-text {
    font-family: "Roboto";
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 14px;
    text-align: center;
    letter-spacing: 0.05em;
    flex-wrap: nowrap;

    &--small {
      height: 35px;
      display: flex;
      align-items: center;
      padding: 5px 10px;
      border-radius: 5px;
      background: var(--color-dark-bg-primary);
    }

    &--area {
      margin-left: 0;
    }
  }
  &-trash {
    margin-left: 15px;
    margin-top: 5px;
  }
  &-container {
    max-height: 50vh;
    overflow: scroll;
    margin: 0;
  }
  &-custom-button {
    width: calc(100% - 24px);
  }
  &-submenu {
    &__title {
      @include typography.getFont($font-name: subtitle1);
      color: black;
    }
    &__header {
      font-family: "Roboto";
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 14px;
      text-align: center;
      letter-spacing: 0.05em;

      &--small {
        font-size: 13px;
      }
    }
    &__subheader {
      font-family: "Roboto";
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 14px;
      text-align: center;
      letter-spacing: 0.05em;
    }
    &__button {
      margin-left: 0;
      display: flex;
      justify-content: center;

      :deep(button) {
        width: 142px;
      }
    }
  }
}

.main-menu {
  background: var(--color-dark-bg-primary);

  &--small {
    background: var(--color-dark-bg-tertiary);
  }
}

.panel--selected {
  background: colors.$HONE200;
}

.position__right {
  position: absolute;
  right: 0;
}

.mt-auto {
  margin-top: auto;
}

.airteam-viewer {
  font-family: "Roboto", sans-serif;
  overflow: hidden;
  margin: 0;
  padding: 0px;
  background: hsl(0, 0%, 30%);

  :deep(canvas) {
    width: 100% !important;
  }
}

.col-p5 {
  width: 2%;
  text-align: center;
  background: var(--color-dark-bg-primary);
}

.col-100 {
  position: relative;
  width: 100%;
}

.col-115 {
  position: relative;
  width: 98%;
}

.position4,
.position5,
.position6,
.position55 {
  position: absolute;
  color: #fff;
  z-index: 15 !important;
}

.add-panels-menu {
  width: 240px;
}

.position4 {
  position: fixed;
  bottom: 14px;
  margin-left: 24px;
  text-align: left;
}

.position5 {
  z-index: 6000 !important;
  top: 50vh;
  transform: translateY(-50%);
  text-align: left;

  &--disabled {
    z-index: 1 !important;
  }
}

.position55 {
  top: 40vh;
  left: 60px;
  transform: translateY(-50%);
  text-align: left;
}

.measurement-menu {
  position: absolute;
  color: #fff;
  z-index: 15 !important;
  text-align: left;
}

.position6 {
  top: 50%;
  transform: translateY(-50%);
  margin-left: 6.7rem;
  text-align: left;
}

.help-button {
  margin: -30px -30px -45px -30px !important;
  border: none;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  outline: none;
  width: 80px;
}

.airteam-logo {
  margin-top: -12px;
}

a {
  color: var(--color-map-green);
}

.button {
  border: none;
  padding: 10px 10px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 2px 2px;
  cursor: pointer;
  outline: none;
}

.add-panels {
  width: 100%;
}

.report-button {
  width: calc(100% - 48px);
  bottom: 40px;
  position: absolute;
}

.add-panels-text {
  margin: 4px 0px;
  color: var(--color-greb400);
  letter-spacing: 1.5px;
  font-size: 8px;
}

.dark {
  background-color: var(--color-viewer-green-darker);
  color: white;
}

.Cclay {
  background-color: var(--color-mode-area);
  color: white;
}

.Cblue {
  background-color: var(--color-mode-measure);
  color: white;
}

.Cgreen {
  background-color: var(--color-mode-solar);
  color: black;
  border-radius: 25px;
}

.Cgrey {
  background-color: var(--color-mode-inactive);
  color: black;
}

.Cred {
  background-color: var(--color-mode-restricted);
  color: white;
}

.green-dot-cursor {
  cursor: url("@/assets/icons/green-dot.svg"), auto;
}

.panel-text {
  color: var(--color-text-muted);
  font-size: 10px;

  &--active {
    color: var(--color-primary);
  }
}

#draggable-container {
  position: absolute;
  z-index: 9;
}
#draggable-header {
  z-index: 10;
  cursor: grab;
}

.close-row {
  margin-top: -15px;
}

.close-menu {
  cursor: pointer;
}

.move-menu {
  position: absolute;
  left: 0;
  margin-left: -40px;
  margin-top: -25px;
}

.position0 {
  position: absolute;
  display: flex;
  top: 1.4em;
  left: 1em;
  text-align: right;
  margin-left: 50px;
}

.undo-redo {
  background: rgba(59, 60, 61, 1);
  border-radius: 8px;
  z-index: 15 !important;
  display: inline-block;
  width: 50px;
  height: 45px;
  text-align: center;
  border: 1px solid rgba(39, 41, 44, 1);
}

.screenshot-bg {
  background-color: rgba(59, 60, 61, 1);
  padding: 0 20px;
  text-align: center;
}

.position1 {
  position: absolute;
  background-color: rgba(59, 60, 61, 1);
  color: colors.$EMER500;
  right: 0;
  border-bottom-left-radius: 8px;
  text-align: right;
  z-index: 150 !important;
}

.position2 {
  position: absolute;
  right: 0;
  top: 60px;
  padding: 0 20px;
  text-align: right;
  z-index: 15 !important;
}

.position9 {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-40%);
  background: var(--color-dark-bg-primary);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  z-index: 15 !important;
  display: flex;
  align-items: center;

  &--sample {
    transform: translateX(-40%);
  }
}

.position10 {
  color: white;
  position: fixed;
  bottom: 16px;
  right: 16px;
  background-color: rgba(238, 238, 238, 0.1);
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
}

.position11 {
  position: fixed;
  bottom: 64px;
  right: 16px;
  background-color: rgba(238, 238, 238, 0.1);
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
}

.cursor-disabled {
  cursor: not-allowed;
  color: colors.$GREY500;
}

.icon {
  cursor: pointer;
}

.height-75 {
  height: 75px;
}

.height-250 {
  height: 250px;
}

.menu-icon {
  padding: 0px;
  margin-bottom: 4px;

  &--small {
    width: 18px;
  }
}

.undo-arrow {
  width: 35px;
  height: 20px;
  position: relative;
  top: 40%;
  transform: translateY(-40%);
}

.orientation-values {
  display: flex;
  flex-direction: row;
}

.orientation-input-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.orientation-label {
  color: white;
  font-weight: 500;
  font-size: 13px;
  margin: 0;
  min-width: 20px;
}

.orientation-input-field {
  border-radius: 2px;
  width: 95px;
  color: white;
}

.orientation-input-field :deep(.el-input__wrapper) {
  position: relative;
  display: inline-block;
  box-shadow: none;
  background-color: var(--color-dark-bg-primary);
  padding-left: 8px;
  padding-right: 39px;
}

.orientation-input-field :deep(.el-input__wrapper)::after {
  content: "°";
  position: absolute;
  top: 50%;
  right: 34px;
  transform: translateY(-50%);
  font-size: 14px;
  color: white;
  pointer-events: none;
}
.orientation-input-field :deep(.el-input__inner) {
  color: white;
  text-align: center;
  border-radius: 2px !important;
  cursor: text;
}

.orientation-input-field :deep(.el-input-number__decrease) {
  box-shadow: none;
  background-color: var(--color-dark-bg-primary);
  border-color: var(--color-dark-bg-secondary) !important;
  color: white;
}

.orientation-input-field :deep(.el-input-number__increase) {
  box-shadow: none;
  background-color: var(--color-dark-bg-primary);
  border-color: var(--color-dark-bg-secondary) !important;
  color: white;
}

.reset-orientation-link {
  color: var(--color-gree300) !important;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--color-map-green);
    text-decoration: none;
  }
}

.orientation-divider {
  border: none;
  border-top: 1px solid var(--color-dark-bg-secondary);
  margin: 16px 0;
}

.camera-toggle-btn {
  width: 100%;
  font-size: 12px;
}

.scale-input-field {
  width: 100%;
  padding: 8px 12px;
  background-color: var(--color-dark-bg-primary);
  color: white;
  border: 1px solid var(--color-dark-bg-secondary);
  border-radius: 4px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: var(--color-gree300);
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
}

.ff3d-btn::before {
  border-color: colors.$GREY300 !important;
}

.menu-text {
  font-weight: normal;
}

.menu-text--active {
  color: var(--color-gree300);
}

.wrapper {
  background: black;
  height: 100vh;
}

.progress-container {
  color: var(--color-grey50);
  text-align: center;
  font-size: 24px;
  font-weight: 700;
}

.progress-hint {
  font-size: 18px;
  font-weight: normal;
}

.thumbnail {
  margin-bottom: 8px;
  width: 500px;
}

#imageContainer {
  z-index: 1000;
  position: absolute;
  display: none;
  pointer-events: none; /* This ensures the container doesn't interfere with mouse events */
}

#zoom-plus,
#zoom-minus {
  position: absolute;
  width: 50px;
  height: 50px;
}

#zoom-plus {
  bottom: 100%; /* Position it above the container's top edge */
  left: 50%; /* Center it horizontally */
  transform: translateX(-50%); /* Adjust for image's own width */
}

#zoom-minus {
  top: 20px; /* Position it below the container's bottom edge */
  left: 50%; /* Center it horizontally */
  transform: translateX(-50%); /* Adjust for image's own width */
}

@media only screen and (max-width: 560px) {
  .hide-mobile {
    display: none;
  }

  .position9 {
    left: 10%;
  }

  .position1 {
    padding: 10px 20px;
  }

  .project-panel {
    z-index: 100;
  }

  .close-sidebar {
    margin-right: 0;
  }

  .open-sidebar {
    margin-left: 20px;
  }
}

.view-plane-button {
  background-color: rgba(59, 60, 61, 1);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  min-width: 50px;
  text-align: center;
  border: 1px solid rgba(39, 41, 44, 1);
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(80, 82, 85, 1);
  }

  &--active {
    background-color: var(--color-gree300);
    color: black;
    border-color: var(--color-gree300);
  }
}
</style>

<style lang="scss">
.share-title {
  font-weight: bold;
  font-size: 13px;
  color: white;
  padding-left: 10px;
}
.share-message-body {
  font-size: 13px;
  color: white;
  padding-left: 30px;
  padding-right: 30px;
}
.share-message-body .valid {
  font-weight: bold;
}
.share-close-button {
  span {
    font-weight: bold;
    font-size: 14px;
    color: var(--color-primary) !important;
  }
}
.el-overlay {
  z-index: 60001 !important;
}
@media (max-width: 1600px) {
  .q-item__label {
    font-size: 12px;
  }
  .q-field__control.relative-position.row.no-wrap {
    padding: 0 3px;
    input {
      text-align: end;
    }
  }
  span {
    font-size: 12px;
  }
}

@media (max-width: 1450px) {
  .q-field__control.relative-position.row.no-wrap {
    padding: 0 1px;
    input {
      text-align: end;
    }
  }
}
@media (max-height: 850px) {
  .new-sidebar-layout {
    .sidebar-bottom {
      max-height: 30rem;
    }
  }
}
.nav-button {
  border-radius: 8px;
  padding: 6px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &--active {
    background-color: var(--color-gree300);
    img, svg {
      filter: brightness(0);
    }
  }
}
</style>
